import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import PanelTopSecond from '../../component/Admin/PanelTopSecond';
import LoadingExtraSmall from '../../component/LoadingExtraSmall';
import {
    FaCheckCircle,
    FaUser,
    FaLock,
    FaUserTag,
    FaBook,
    FaSave,
    FaIdBadge,
} from 'react-icons/fa';

const emptyForm = {
    username: '',
    nama: '',
    password: '',
    level: '',
    journalIds: [],
};

const fieldClass =
    'block w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

const labelClass =
    'mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400';

const UserForm = ({ data }) => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const isEdit = Boolean(userId);
    const header = { headers: { Authorization: `Bearer ${data.token}` } };

    const [form, setForm] = useState(emptyForm);
    const [listJournal, setListJournal] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [loadingUsername, setLoadingUsername] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(null);

    useEffect(() => {
        axios
            .get('api/journal?page=1&limit=200', header)
            .then((res) => setListJournal(res.data.items || []))
            .catch(() => setListJournal([]));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!isEdit || !userId) return;
        setLoading(true);
        axios
            .get(`api/admin/id/${userId}`, header)
            .then((res) => {
                const item = res.data;
                if (!item?.id) {
                    Swal.fire('Gagal', 'User tidak ditemukan', 'error');
                    navigate('/admin/users');
                    return;
                }
                setForm({
                    username: item.username || '',
                    nama: item.nama || '',
                    password: '',
                    level: item.level || '',
                    journalIds: (item.journalIds || item.journals?.map((j) => j.journal_id) || []).map(
                        Number
                    ),
                });
            })
            .catch(() => {
                Swal.fire('Gagal', 'Tidak bisa memuat data user', 'error');
                navigate('/admin/users');
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [userId, isEdit]);

    const toggleJournal = (journalId) => {
        const id = Number(journalId);
        setForm((prev) => {
            const exists = prev.journalIds.includes(id);
            return {
                ...prev,
                journalIds: exists
                    ? prev.journalIds.filter((x) => x !== id)
                    : [...prev.journalIds, id],
            };
        });
    };

    const checkUsernameAvailability = (username) => {
        if (!username || isEdit) return;
        setLoadingUsername(true);
        axios
            .get(`api/admin/users/${username}`, header)
            .then((response) => {
                setUsernameAvailable(response.data);
            })
            .catch(() => setUsernameAvailable(null))
            .finally(() => setLoadingUsername(false));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.level) {
            Swal.fire('Gagal!', 'Role wajib dipilih.', 'error');
            return;
        }
        if (form.level === 'operator' && !form.journalIds.length) {
            Swal.fire('Gagal!', 'Operator wajib di-assign minimal 1 journal.', 'error');
            return;
        }
        if (!isEdit && usernameAvailable === false) {
            Swal.fire('Gagal!', 'Username sudah digunakan.', 'error');
            return;
        }

        const payload = {
            nama: form.nama,
            level: form.level,
            journalIds: form.level === 'admin' ? [] : form.journalIds,
        };
        if (form.password) payload.password = form.password;

        setSaving(true);

        const request = isEdit
            ? axios.patch(`api/admin/${userId}`, payload, header)
            : axios.post(
                  'api/admin',
                  {
                      ...payload,
                      username: form.username,
                      password: form.password,
                  },
                  header
              );

        request
            .then(() => {
                if (typeof data?.refreshAuth === 'function') {
                    data.refreshAuth();
                } else {
                    window.dispatchEvent(new Event('auth:refresh'));
                }
                Swal.fire({
                    title: 'Berhasil',
                    text: isEdit ? 'User berhasil diperbarui.' : 'User berhasil ditambahkan.',
                    icon: 'success',
                    timer: 1200,
                    showConfirmButton: false,
                }).then(() => navigate('/admin/users'));
            })
            .catch(() => {
                Swal.fire(
                    'Gagal!',
                    isEdit
                        ? 'Terjadi kesalahan saat memperbarui data.'
                        : 'Terjadi kesalahan saat menambahkan data.',
                    'error'
                );
            })
            .finally(() => setSaving(false));
    };

    const rolePreview =
        form.level === 'admin'
            ? 'Akses penuh ke seluruh journal'
            : form.level === 'operator'
              ? `${form.journalIds.length || 0} journal dipilih`
              : 'Pilih role user';

    if (loading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center text-sm text-gray-500">
                Memuat data user...
            </div>
        );
    }

    return (
        <>
            <PanelTopSecond
                link="/admin/users"
                title={isEdit ? 'Edit User' : 'Tambah User'}
            />

            <form onSubmit={handleSubmit} className="space-y-4 pb-8">
                <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                    {/* Side summary */}
                    <aside className="h-fit overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                        <div className="bg-gradient-to-br from-primary to-primary-focus px-5 py-6 text-white">
                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-xl">
                                <FaIdBadge />
                            </div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                                {isEdit ? 'Edit akun' : 'User baru'}
                            </p>
                            <h2 className="mt-1 truncate text-lg font-bold">
                                {form.nama || form.username || '—'}
                            </h2>
                            <p className="mt-0.5 truncate text-sm text-white/75">
                                @{form.username || 'username'}
                            </p>
                        </div>
                        <div className="space-y-3 px-5 py-4 text-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-gray-500 dark:text-gray-400">Role</span>
                                <span
                                    className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                                        form.level === 'admin'
                                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                            : form.level === 'operator'
                                              ? 'bg-primary/10 text-primary dark:text-primary-on-dark'
                                              : 'bg-gray-100 text-gray-400 dark:bg-gray-700'
                                    }`}
                                >
                                    {form.level || 'Belum dipilih'}
                                </span>
                            </div>
                            <div className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                                {rolePreview}
                            </div>
                        </div>
                    </aside>

                    {/* Form fields */}
                    <div className="space-y-4">
                        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100">
                                <FaUser className="text-primary" />
                                Informasi akun
                            </h3>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <label className={labelClass}>
                                        Username <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        disabled={isEdit}
                                        type="text"
                                        required={!isEdit}
                                        value={form.username}
                                        onChange={(e) => {
                                            setForm({ ...form, username: e.target.value });
                                            setUsernameAvailable(null);
                                        }}
                                        onBlur={(e) => checkUsernameAvailability(e.target.value)}
                                        placeholder="Masukan username"
                                        className={`${fieldClass} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-900/50 ${
                                            usernameAvailable === false ? 'border-red-400' : ''
                                        }`}
                                    />
                                    <div className="mt-1.5 flex min-h-[18px] items-center gap-1.5 text-xs">
                                        {loadingUsername && <LoadingExtraSmall />}
                                        {usernameAvailable === false && (
                                            <span className="flex items-center gap-1 text-red-500">
                                                <FaCheckCircle /> Username sudah digunakan.
                                            </span>
                                        )}
                                        {usernameAvailable === true && (
                                            <span className="flex items-center gap-1 text-emerald-600">
                                                <FaCheckCircle /> Username tersedia.
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="sm:col-span-2">
                                    <label className={labelClass}>
                                        Nama <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={form.nama}
                                        onChange={(e) => setForm({ ...form, nama: e.target.value })}
                                        placeholder="Nama lengkap"
                                        className={fieldClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>
                                        <FaLock className="opacity-70" />
                                        Password
                                        {!isEdit && <span className="text-red-400">*</span>}
                                    </label>
                                    <input
                                        type="password"
                                        required={!isEdit}
                                        value={form.password}
                                        onChange={(e) =>
                                            setForm({ ...form, password: e.target.value })
                                        }
                                        placeholder={
                                            isEdit ? 'Kosongkan jika tidak diubah' : '••••••••'
                                        }
                                        className={fieldClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>
                                        <FaUserTag className="opacity-70" />
                                        Role <span className="text-red-400">*</span>
                                    </label>
                                    <select
                                        required
                                        value={form.level}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                level: e.target.value,
                                                journalIds:
                                                    e.target.value === 'admin'
                                                        ? []
                                                        : form.journalIds,
                                            })
                                        }
                                        className={fieldClass}
                                    >
                                        <option value="">Pilih Role</option>
                                        <option value="admin">Admin</option>
                                        <option value="operator">Operator</option>
                                    </select>
                                </div>
                            </div>
                        </section>

                        {form.level === 'operator' && (
                            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                                <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                                    <div>
                                        <h3 className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100">
                                            <FaBook className="text-primary" />
                                            Assign Journal
                                            <span className="text-red-400">*</span>
                                        </h3>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Operator hanya bisa akses journal yang dipilih.
                                        </p>
                                    </div>
                                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary dark:text-primary-on-dark">
                                        {form.journalIds.length} dipilih
                                    </span>
                                </div>

                                <div className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-900/40">
                                    {listJournal.length ? (
                                        listJournal.map((j) => {
                                            const checked = form.journalIds.includes(
                                                Number(j.journal_id)
                                            );
                                            return (
                                                <label
                                                    key={j.journal_id}
                                                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-gray-800 hover:bg-white dark:text-gray-200 dark:hover:bg-gray-800"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleJournal(j.journal_id)}
                                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                    />
                                                    <span className="truncate">{j.journal_name}</span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <p className="px-2 py-1 text-xs text-gray-400">
                                            Tidak ada journal.
                                        </p>
                                    )}
                                </div>
                            </section>
                        )}

                        {form.level === 'admin' && (
                            <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary dark:text-primary-on-dark">
                                Admin otomatis punya akses ke semua journal.
                            </div>
                        )}

                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/users')}
                                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-600 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center justify-center gap-2 rounded-xl btn-primary disabled:opacity-50"
                            >
                                <FaSave />
                                {saving ? 'Menyimpan...' : 'Simpan'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default UserForm;
